import { useEffect, useState, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../lib/AuthContext';
import orangeLogo from '../assets/orangelog.png';

const T = {
  accent:  '#dd3333',
  header:  '#111111',
  border:  '#e8e8e8',
  bodyText:'#444',
  headText:'#111',
  meta:    '#777',
  lightBg: '#f4f4f4',
  white:   '#fff',
};

const GENRE_COLOR = {
  'National News': '#dd3333',
  'State News':    '#4db2ec',
  'Local News':    '#27ae60',
  'Sports':        '#e67e22',
  'Technology':    '#8e44ad',
};

const GENRE_ROUTES = [
  { label: 'National News', to: '/national-news', color: '#dd3333' },
  { label: 'State News',    to: '/state-news',    color: '#4db2ec' },
  { label: 'Sports',        to: '/sports',        color: '#e67e22' },
  { label: 'Technology',    to: '/technology',    color: '#8e44ad' },
  { label: 'Local News',    to: '/local-news',    color: '#27ae60' },
];

function fmtDate(d) {
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function goArticle(navigate, article, token) {
  const dest = `/${encodeURIComponent(article.company_name)}?a=${article._id}`;
  if (!token) { navigate(`/login?next=${encodeURIComponent(dest)}`); return; }
  navigate(dest);
}

function Skeleton({ h = 20, w = '100%', style: s = {} }) {
  return <div style={{ height: h, width: w, background: '#e8e8e8', borderRadius: '3px', ...s }} />;
}

/* ── Hero card ── */
function HeroCard({ article, onClick }) {
  const [hov, setHov] = useState(false);
  const color = GENRE_COLOR[article.genre_tree?.main] || T.accent;
  return (
    <div onClick={onClick} onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={{ cursor: 'pointer', height: '100%', minHeight: '480px', position: 'relative', overflow: 'hidden', background: '#111' }}>
      {article.headline_image
        ? <img src={article.headline_image} alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top', transition: 'transform 0.5s', transform: hov ? 'scale(1.04)' : 'scale(1)' }} />
        : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <span style={{ fontSize: '4rem', opacity: 0.1 }}>📰</span>
          </div>
      }
      <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.88) 0%, rgba(0,0,0,0.25) 55%, transparent 100%)' }} />
      <div style={{ position: 'absolute', bottom: 0, left: 0, padding: '28px', right: 0 }}>
        <div style={{ display: 'inline-block', fontSize: '10px', fontWeight: '900', letterSpacing: '0.1em', textTransform: 'uppercase', color: T.white, background: color, padding: '3px 8px', marginBottom: '10px', fontFamily: 'Arial, sans-serif' }}>
          {article.genre_tree?.main}
        </div>
        <div style={{ fontSize: '28px', fontWeight: '900', color: T.white, lineHeight: '35px', marginBottom: '8px', fontFamily: 'Georgia, serif' }}>
          {article.title}
        </div>
        <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.65)', fontFamily: 'Arial, sans-serif' }}>
          {article.company_name} · {fmtDate(article.date_created)}
        </div>
      </div>
    </div>
  );
}

/* ── Grid card (2×2 right side) ── */
function GridCard({ article, onClick }) {
  const [hov, setHov] = useState(false);
  const color = GENRE_COLOR[article.genre_tree?.main] || T.accent;
  return (
    <div onClick={onClick} onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={{ cursor: 'pointer', background: '#fff', border: '1px solid rgba(0,0,0,0.1)', borderRadius: '6px', overflow: 'hidden', boxShadow: hov ? '0 6px 18px rgba(0,0,0,0.13)' : '0 1px 4px rgba(0,0,0,0.07)', transition: 'box-shadow 0.2s', display: 'flex', flexDirection: 'column' }}>
      <div style={{ aspectRatio: '16/9', overflow: 'hidden', flexShrink: 0, background: '#e8e8e8' }}>
        {article.headline_image
          ? <img src={article.headline_image} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top', transition: 'transform 0.4s', transform: hov ? 'scale(1.04)' : 'scale(1)' }} />
          : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <span style={{ opacity: 0.15, fontSize: '2rem' }}>📰</span>
            </div>
        }
      </div>
      <div style={{ padding: '0.6rem 0.75rem', flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
        <div>
          <div style={{ fontSize: '10px', fontWeight: '800', color, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '4px', fontFamily: 'Arial, sans-serif' }}>
            {article.genre_tree?.main}
          </div>
          <div style={{ fontSize: '13px', fontWeight: '700', color: hov ? color : T.headText, lineHeight: '19px', transition: 'color 0.14s', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden', fontFamily: 'Georgia, serif' }}>
            {article.title}
          </div>
        </div>
        <div style={{ fontSize: '11px', color: T.meta, marginTop: '5px', fontFamily: 'Arial, sans-serif' }}>
          {article.company_name} · {fmtDate(article.date_created)}
        </div>
      </div>
    </div>
  );
}

/* ── Carousel card ── */
function CarouselCard({ article, onClick, accentColor }) {
  const [hov, setHov] = useState(false);
  return (
    <div onClick={onClick} onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={{ cursor: 'pointer' }}>
      <div style={{ aspectRatio: '16/9', overflow: 'hidden', background: '#e8e8e8', marginBottom: '10px' }}>
        {article.headline_image
          ? <img src={article.headline_image} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top', transition: 'transform 0.4s', transform: hov ? 'scale(1.04)' : 'scale(1)' }} />
          : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <span style={{ opacity: 0.15, fontSize: '2rem' }}>📰</span>
            </div>
        }
      </div>
      <div style={{ fontSize: '10px', fontWeight: '800', color: accentColor, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '4px', fontFamily: 'Arial, sans-serif' }}>
        {article.genre_tree?.main || 'Trending'}
      </div>
      <div style={{ fontSize: '14px', fontWeight: '700', color: hov ? accentColor : T.headText, lineHeight: '20px', transition: 'color 0.14s', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', fontFamily: 'Georgia, serif' }}>
        {article.title}
      </div>
      <div style={{ fontSize: '11px', color: T.meta, marginTop: '5px', fontFamily: 'Arial, sans-serif' }}>
        {article.company_name} · {fmtDate(article.date_created)}
      </div>
    </div>
  );
}

/* ── Nav search widget (desktop) ── */
function NavSearchWidget({ searchQuery, setSearchQuery, setSearchResults }) {
  const [expanded, setExpanded] = useState(false);
  const inputRef = useRef(null);
  const active = expanded || searchQuery.length > 0;

  function expand() {
    setExpanded(true);
    setTimeout(() => inputRef.current?.focus(), 30);
  }
  function collapse() {
    if (!searchQuery) setExpanded(false);
  }

  return (
    <div
      onMouseEnter={expand}
      onMouseLeave={collapse}
      style={{
        display: 'flex', alignItems: 'center', height: '26px',
        width: active ? '136px' : '26px',
        transition: 'width 0.28s ease, background 0.2s, border-color 0.2s',
        overflow: 'hidden', borderRadius: '4px',
        background: active ? 'rgba(255,255,255,0.1)' : 'transparent',
        border: active ? '1px solid rgba(255,255,255,0.22)' : '1px solid transparent',
        cursor: 'pointer',
      }}
    >
      <span style={{ fontSize: '13px', padding: '0 5px 0 6px', color: active ? '#ddd' : '#777', flexShrink: 0, lineHeight: 1, transition: 'color 0.2s' }}>
        🔍
      </span>
      <input
        ref={inputRef}
        type="text"
        value={searchQuery}
        onChange={e => setSearchQuery(e.target.value)}
        onFocus={() => setExpanded(true)}
        onBlur={collapse}
        placeholder="Search…"
        style={{ flex: 1, minWidth: 0, background: 'transparent', border: 'none', outline: 'none', color: '#fff', fontSize: '12px', fontFamily: 'Arial, sans-serif', padding: '0', opacity: active ? 1 : 0, transition: 'opacity 0.2s' }}
      />
      {searchQuery && (
        <button onClick={() => { setSearchQuery(''); setSearchResults(null); }}
          style={{ background: 'none', border: 'none', color: '#888', cursor: 'pointer', padding: '0 5px', fontSize: '15px', flexShrink: 0, lineHeight: 1 }}
        >×</button>
      )}
    </div>
  );
}

/* ── Search result article row ── */
function SearchArticleRow({ article, go }) {
  const [hov, setHov] = useState(false);
  const color = GENRE_COLOR[article.genre_tree?.main] || T.accent;
  return (
    <div onClick={() => go(article)} onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={{ display: 'flex', gap: '12px', padding: '12px 0', borderBottom: '1px solid #f0f0f0', cursor: 'pointer' }}>
      {article.headline_image && (
        <div style={{ width: '80px', height: '54px', flexShrink: 0, overflow: 'hidden', borderRadius: '4px', background: '#e8e8e8' }}>
          <img src={article.headline_image} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top' }} />
        </div>
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: '10px', fontWeight: '800', color, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '3px', fontFamily: 'Arial, sans-serif' }}>
          {article.genre_tree?.main}
        </div>
        <div style={{ fontSize: '14px', fontWeight: '700', color: hov ? color : T.headText, lineHeight: '19px', fontFamily: 'Georgia, serif', transition: 'color 0.12s', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
          {article.title}
        </div>
        <div style={{ fontSize: '11px', color: T.meta, marginTop: '3px', fontFamily: 'Arial, sans-serif' }}>
          {article.company_name} · {fmtDate(article.date_created)}
        </div>
      </div>
    </div>
  );
}

/* ── Search result section ── */
function SearchSection({ title, color, items, children }) {
  if (!items || items.length === 0) return null;
  return (
    <div style={{ marginBottom: '32px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
        <div style={{ background: color, color: '#fff', padding: '4px 14px', fontSize: '10px', fontWeight: '900', textTransform: 'uppercase', letterSpacing: '0.1em', fontFamily: 'Arial, sans-serif', borderRadius: '3px' }}>
          {title}
        </div>
        <div style={{ fontSize: '12px', color: '#bbb', fontFamily: 'Arial, sans-serif' }}>{items.length} result{items.length !== 1 ? 's' : ''}</div>
      </div>
      {children}
    </div>
  );
}

/* ── Search overlay ── */
function SearchOverlay({ results, loading, query, onClose, go, isMobile }) {
  const navH = isMobile ? 112 : 146;
  const hasResults = results && (results.companies.length || results.titles.length || results.topics.length);
  return (
    <div style={{ position: 'fixed', top: navH, left: 0, right: 0, bottom: 0, zIndex: 998, background: '#fff', overflowY: 'auto' }}>
      <div style={{ position: 'sticky', top: 0, background: '#fff', borderBottom: '2px solid #eee', padding: '14px 32px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', zIndex: 1, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
        <span style={{ fontSize: '13px', color: '#888', fontFamily: 'Arial, sans-serif' }}>
          Results for <strong style={{ color: '#111' }}>"{query}"</strong>
        </span>
        <button onClick={onClose}
          style={{ width: '32px', height: '32px', borderRadius: '50%', border: '1.5px solid #ddd', background: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px', color: '#555', lineHeight: 1 }}
          onMouseEnter={e => { e.currentTarget.style.background = '#f4f4f4'; }}
          onMouseLeave={e => { e.currentTarget.style.background = '#fff'; }}
        >×</button>
      </div>

      <div style={{ maxWidth: '860px', margin: '0 auto', padding: '28px 32px 60px' }}>
        {loading && (
          <div style={{ textAlign: 'center', padding: '60px 0', color: '#aaa', fontFamily: 'Arial, sans-serif', fontSize: '14px' }}>Searching...</div>
        )}
        {!loading && results && !hasResults && (
          <div style={{ textAlign: 'center', padding: '60px 0', color: '#aaa', fontFamily: 'Arial, sans-serif', fontSize: '14px' }}>No results found for "{query}"</div>
        )}
        {!loading && results && (
          <>
            <SearchSection title="Companies" color="#13273F" items={results.companies}>
              {results.companies.map(c => (
                <Link key={c._id} to={`/${encodeURIComponent(c.company_name)}`}
                  style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '12px 0', borderBottom: '1px solid #f0f0f0', textDecoration: 'none' }}
                >
                  <div style={{ width: '38px', height: '38px', borderRadius: '50%', background: '#13273F', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '16px', fontWeight: '900', fontFamily: 'Arial, sans-serif', flexShrink: 0 }}>
                    {c.company_name[0].toUpperCase()}
                  </div>
                  <div style={{ fontSize: '15px', fontWeight: '700', color: '#111', fontFamily: 'Georgia, serif' }}>{c.company_name}</div>
                </Link>
              ))}
            </SearchSection>
            <SearchSection title="Titles" color="#27ae60" items={results.titles}>
              {results.titles.map(a => <SearchArticleRow key={a._id} article={a} go={go} />)}
            </SearchSection>
            <SearchSection title="Topics" color="#e67e22" items={results.topics}>
              {results.topics.map(a => <SearchArticleRow key={a._id} article={a} go={go} />)}
            </SearchSection>
          </>
        )}
      </div>
    </div>
  );
}

/* ── Publisher card ── */
function PublisherCard({ publisher, go }) {
  const [openIdx, setOpenIdx] = useState(0);
  const hue = publisher.name.split('').reduce((acc, c) => acc + c.charCodeAt(0), 0) % 360;
  const placeholderColor = `hsl(${hue}, 42%, 32%)`;

  return (
    <div style={{ background: '#fff', border: '1px solid rgba(0,0,0,0.12)', borderRadius: '8px', overflow: 'hidden', boxShadow: '0 1px 5px rgba(0,0,0,0.07)', display: 'flex', flexDirection: 'column' }}>

      {/* Logo */}
      <div style={{ height: '130px', background: '#f0f0f0', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {publisher.logo
          ? <img src={publisher.logo} alt={publisher.name} style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center' }} />
          : <div style={{ width: '68px', height: '68px', borderRadius: '50%', background: placeholderColor, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '26px', fontWeight: '900', fontFamily: 'Arial, sans-serif' }}>
              {publisher.name[0].toUpperCase()}
            </div>
        }
      </div>

      {/* Card body */}
      <div style={{ padding: '12px 14px 8px' }}>
        <div style={{ fontSize: '15px', fontWeight: '800', color: '#111', fontFamily: 'Georgia, serif', marginBottom: '3px' }}>{publisher.name}</div>
        <div style={{ fontSize: '11px', color: '#999', fontFamily: 'Arial, sans-serif' }}>{publisher.articles.length} article{publisher.articles.length !== 1 ? 's' : ''} available</div>
      </div>

      {/* Article accordion */}
      <div style={{ borderTop: '1px solid #eee', flex: 1 }}>
        {publisher.articles.map((a, i) => (
          <AccordionRow key={a._id} article={a} open={i === openIdx} onToggle={() => setOpenIdx(i === openIdx ? -1 : i)} go={go} />
        ))}
      </div>

      {/* Footer link */}
      <div style={{ padding: '10px 14px', borderTop: '1px solid #eee' }}>
        <Link to={`/${encodeURIComponent(publisher.name)}`}
          style={{ fontSize: '12px', fontWeight: '700', color: T.accent, textDecoration: 'none', fontFamily: 'Arial, sans-serif' }}
          onMouseEnter={e => e.currentTarget.style.textDecoration = 'underline'}
          onMouseLeave={e => e.currentTarget.style.textDecoration = 'none'}
        >View Silo →</Link>
      </div>
    </div>
  );
}

function AccordionRow({ article, open, onToggle, go }) {
  const [hov, setHov] = useState(false);
  return (
    <div style={{ borderBottom: '1px solid #f0f0f0' }}>
      <div
        onClick={onToggle}
        onMouseEnter={() => setHov(true)}
        onMouseLeave={() => setHov(false)}
        style={{ padding: '9px 14px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px', background: open || hov ? '#f8f8f8' : '#fff', transition: 'background 0.1s' }}
      >
        <span style={{ fontSize: '12px', fontWeight: '700', color: '#222', lineHeight: '17px', fontFamily: 'Georgia, serif', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', flex: 1 }}>
          {article.title}
        </span>
        <span style={{ fontSize: '9px', color: '#aaa', flexShrink: 0, marginTop: '3px', fontFamily: 'Arial, sans-serif' }}>
          {open ? '▲' : '▼'}
        </span>
      </div>
      {open && (
        <div style={{ padding: '8px 14px 10px', background: '#f8f8f8', borderTop: '1px solid #eee' }}>
          <div style={{ fontSize: '11px', color: '#999', fontFamily: 'Arial, sans-serif', marginBottom: '8px' }}>
            {fmtDate(article.date_created)} · {article.token_cost}
          </div>
          <button onClick={() => go(article)}
            style={{ fontSize: '11px', fontWeight: '800', color: '#fff', background: T.accent, border: 'none', borderRadius: '4px', padding: '5px 12px', cursor: 'pointer', fontFamily: 'Arial, sans-serif', letterSpacing: '0.04em' }}
          >Read →</button>
        </div>
      )}
    </div>
  );
}

/* ── Section divider: colored HR + centered label rectangle ── */
function SectionDivider({ label, color, margin = '48px 50px 28px' }) {
  return (
    <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', margin }}>
      <div style={{ position: 'absolute', left: 0, right: 0, height: '2px', background: color }} />
      <div style={{ position: 'relative', zIndex: 1 }}>
        <div style={{ background: color, color: '#fff', padding: '7px 22px', fontSize: '11px', fontWeight: '900', textTransform: 'uppercase', letterSpacing: '0.1em', fontFamily: 'Arial, sans-serif', whiteSpace: 'nowrap' }}>
          {label}
        </div>
      </div>
    </div>
  );
}

/* ── Page badge (numbered) ── */
function PageBadge({ label, color, active, onClick }) {
  const [hov, setHov] = useState(false);
  const filled = active || hov;
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        padding: '4px 11px',
        fontSize: '11px',
        fontWeight: '800',
        fontFamily: 'Arial, sans-serif',
        letterSpacing: '0.05em',
        border: `1.5px solid ${color}`,
        borderRadius: '4px',
        cursor: 'pointer',
        background: filled ? color : '#fff',
        color: filled ? '#fff' : color,
        transition: 'background 0.15s, color 0.15s',
        lineHeight: 1.4,
        outline: 'none',
      }}
    >{label}</button>
  );
}

/* ── "All" badge (link) ── */
function AllBadge({ to, color }) {
  const [hov, setHov] = useState(false);
  return (
    <Link
      to={to}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        padding: '4px 11px',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '11px',
        fontWeight: '900',
        fontFamily: 'Arial, sans-serif',
        border: `1.5px solid ${color}`,
        borderRadius: '4px',
        cursor: 'pointer',
        background: hov ? color : '#fff',
        color: hov ? '#fff' : color,
        transition: 'background 0.15s, color 0.15s',
        textDecoration: 'none',
        lineHeight: 1.4,
      }}
    >❯</Link>
  );
}

/* ── Section carousel: 4 visible, auto-rotate + arrows ── */
function SectionCarousel({ articles, color, go, max = 8, viewAllTo }) {
  const capped = articles.slice(0, max);
  const pages  = Math.max(1, Math.ceil(capped.length / 4));
  const [page, setPage] = useState(0);

  const visible = capped.slice(page * 4, page * 4 + 4);

  if (capped.length === 0) {
    return (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px' }}>
        {[...Array(4)].map((_, i) => (
          <div key={i}>
            <Skeleton h={160} s={{ marginBottom: '10px' }} />
            <Skeleton h={10} w="40%" s={{ marginBottom: '6px' }} />
            <Skeleton h={14} />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px' }}>
        {visible.map(a => (
          <CarouselCard key={a._id} article={a} accentColor={color} onClick={() => go(a)} />
        ))}
      </div>

      {(pages > 1 || viewAllTo) && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', marginTop: '20px' }}>
          {[...Array(pages)].map((_, i) => {
            const active = i === page;
            return (
              <PageBadge
                key={i}
                label={String(i + 1)}
                color={color}
                active={active}
                onClick={() => setPage(i)}
              />
            );
          })}
          {viewAllTo && (
            <AllBadge to={viewAllTo} color={color} />
          )}
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════════
   Main component
══════════════════════════════════════════════════════════════════════════════ */
export default function LandingPage8() {
  const navigate = useNavigate();
  const { accessToken: authToken } = useAuth();
  const userType    = localStorage.getItem('userType');
  const username    = localStorage.getItem('username');
  const companySlug = localStorage.getItem('companyName');
  const isAdmin     = localStorage.getItem('isAdmin') === 'true';

  const [genreArticles, setGenreArticles]     = useState({});
  const [trendingArticles, setTrendingArticles] = useState([]);
  const [tokenBalance, setTokenBalance]       = useState(null);
  const [loading, setLoading]                 = useState(true);
  const [now, setNow]                         = useState(new Date());
  const [tokenDropOpen, setTokenDropOpen]     = useState(false);
  const [searchQuery, setSearchQuery]         = useState('');
  const [searchResults, setSearchResults]     = useState(null);
  const [searchLoading, setSearchLoading]     = useState(false);
  const [isMobile, setIsMobile]               = useState(() => window.innerWidth < 768);
  const tokenDropRef = useRef(null);

  const siloHref = (() => {
    if (!authToken) return null;
    if (isAdmin)                return '/admin';
    if (userType === 'company') return `/adminHUD/${encodeURIComponent(companySlug)}`;
    if (userType === 'author')  return `/${encodeURIComponent(companySlug)}/author/${encodeURIComponent(username)}`;
    if (userType === 'editor')  return `/${encodeURIComponent(companySlug)}/editor/${encodeURIComponent(username)}`;
    if (userType === 'user')    return `/${username}`;
    return null;
  })();

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    if (!tokenDropOpen) return;
    const handler = (e) => {
      if (tokenDropRef.current && !tokenDropRef.current.contains(e.target)) setTokenDropOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [tokenDropOpen]);

  useEffect(() => {
    const q = searchQuery.trim();
    if (q.length < 2) { setSearchResults(null); setSearchLoading(false); return; }
    setSearchLoading(true);
    const t = setTimeout(() => {
      axios.get(`/api/articles/search?q=${encodeURIComponent(q)}`)
        .then(r => { setSearchResults(r.data); setSearchLoading(false); })
        .catch(() => setSearchLoading(false));
    }, 350);
    return () => clearTimeout(t);
  }, [searchQuery]);

  useEffect(() => {
    const headers = authToken ? { Authorization: `Bearer ${authToken}` } : {};
    axios.get('/api/articles/trending').then(r => setTrendingArticles(r.data || [])).catch(() => {});
    axios.get('/api/articles/landing', { headers })
      .then(r => setGenreArticles(r.data || {}))
      .catch(() => {})
      .finally(() => setLoading(false));
    if (authToken && userType === 'user') {
      axios.get('/api/users/me', { headers }).then(r => setTokenBalance(r.data.tokens ?? 0)).catch(() => {});
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const allArticles   = Object.values(genreArticles).flat();
  const heroArticle   = allArticles.find(a => a.headline_image) || allArticles[0] || null;
  const sideArticles  = allArticles.filter(a => a._id !== heroArticle?._id).slice(0, 4);
  const go = (article) => goArticle(navigate, article, authToken);

  const publishers = Object.values(
    allArticles.reduce((acc, a) => {
      if (!acc[a.company_name]) acc[a.company_name] = { name: a.company_name, logo: a.company_logo || null, articles: [] };
      if (acc[a.company_name].articles.length < 4) acc[a.company_name].articles.push(a);
      return acc;
    }, {})
  ).filter(p => p.articles.length >= 1).slice(0, 8);

  const localArticles    = (genreArticles['Local News']    || []).slice(0, 8);
  const stateArticles    = (genreArticles['State News']    || []).slice(0, 8);
  const nationalArticles = (genreArticles['National News'] || []).slice(0, 8);
  const trendSlice       = trendingArticles.slice(0, 12);

  return (
    <div style={{ minHeight: '100vh', background: T.white, fontFamily: "'Georgia', serif", color: T.bodyText }}>

      {/* ── Navbar (identical to LandingPage5) ── */}
      {isMobile ? (
        <nav style={{ position: 'sticky', top: 0, zIndex: 1000, boxShadow: '0 2px 12px rgba(0,0,0,0.6)' }}>
          <div style={{ display: 'flex', height: '70px' }}>
            <div style={{ width: '33%', background: '#F41238', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <img src={orangeLogo} alt="PubSilo" style={{ height: '90%', maxWidth: '100%', objectFit: 'contain', cursor: 'pointer' }} onClick={() => navigate('/')} />
            </div>
            <div style={{ flex: 1, background: '#13273F', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
              <div style={{ color: T.white, fontSize: '20px', fontWeight: '700', fontFamily: 'Georgia, serif', lineHeight: 1 }}>PubSilo</div>
              <div style={{ color: '#aaa', fontSize: '10px', fontFamily: 'Arial, sans-serif', marginTop: '3px' }}>{now.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</div>
            </div>
          </div>
          <div style={{ background: '#0d1f33', height: '42px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 14px', borderTop: '1px solid #1a2f45' }}>
            {authToken ? (
              <>
                {userType === 'user' && tokenBalance !== null && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '5px', background: '#1e1e1e', border: '1px solid #333', borderRadius: '5px', padding: '5px 10px', color: T.white, fontSize: '12px', fontFamily: 'Arial, sans-serif', cursor: 'pointer' }} onClick={() => navigate('/tokens/purchase')}>
                    <span>🪙</span><span style={{ fontWeight: '700' }}>{tokenBalance}</span><span style={{ fontSize: '10px', color: T.accent, marginLeft: '2px', fontWeight: '700' }}>+ Buy</span>
                  </div>
                )}
                {siloHref && <Link to={siloHref} style={{ background: T.accent, color: T.white, padding: '6px 14px', borderRadius: '4px', textDecoration: 'none', fontSize: '12px', fontWeight: '700', fontFamily: 'Arial, sans-serif', whiteSpace: 'nowrap' }}>{userType === 'company' ? 'Admin Page' : 'My Silo'}</Link>}
              </>
            ) : (
              <>
                <Link to="/login" style={{ color: '#ccc', textDecoration: 'none', fontSize: '12px', fontFamily: 'Arial, sans-serif', fontWeight: '700', padding: '6px 12px', border: '1px solid #3a3a3a', borderRadius: '4px' }}>Log In</Link>
                <Link to="/signup/role" style={{ background: T.accent, color: T.white, textDecoration: 'none', fontSize: '12px', fontFamily: 'Arial, sans-serif', fontWeight: '700', padding: '6px 14px', borderRadius: '4px', whiteSpace: 'nowrap' }}>Sign Up</Link>
              </>
            )}
          </div>
        </nav>
      ) : (
        <nav style={{ background: T.header, display: 'grid', gridTemplateColumns: '220px 1fr 300px', gridTemplateRows: '110px 36px', position: 'sticky', top: 0, zIndex: 1000, boxShadow: '0 2px 12px rgba(0,0,0,0.6)' }}>
          <div style={{ gridColumn: 1, gridRow: '1 / 3', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#F41238' }}>
            <img src={orangeLogo} alt="PubSilo" style={{ height: '90%', maxWidth: '210px', objectFit: 'contain', cursor: 'pointer' }} onClick={() => navigate('/')} />
          </div>
          <div style={{ gridColumn: 2, gridRow: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#13273F' }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ color: '#aaa', fontSize: '13px', fontFamily: 'Arial, sans-serif', letterSpacing: '0.03em', marginBottom: '4px' }}>
                {now.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
              </div>
              <div style={{ color: T.white, fontSize: '44px', fontWeight: '300', fontFamily: 'Arial, sans-serif', letterSpacing: '0.08em', lineHeight: 1 }}>
                {now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
              </div>
            </div>
          </div>
          <div style={{ gridColumn: 3, gridRow: '1 / 3', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', background: '#13273F' }}>
            {authToken ? (
              <>
                {userType === 'user' && tokenBalance !== null && (
                  <div ref={tokenDropRef} style={{ position: 'relative' }}>
                    <div onClick={() => setTokenDropOpen(o => !o)} style={{ display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer', background: '#1e1e1e', border: '1px solid #333', borderRadius: '6px', padding: '7px 13px', color: T.white, fontSize: '13px', fontFamily: 'Arial, sans-serif', userSelect: 'none' }}>
                      <span>🪙</span><span style={{ fontWeight: '700' }}>{tokenBalance}</span><span style={{ fontSize: '9px', color: '#777', marginLeft: '2px' }}>▼</span>
                    </div>
                    {tokenDropOpen && (
                      <div style={{ position: 'absolute', top: 'calc(100% + 6px)', right: 0, background: '#1a1a1a', border: '1px solid #2e2e2e', borderRadius: '8px', padding: '10px 0', minWidth: '160px', zIndex: 1001, boxShadow: '0 6px 20px rgba(0,0,0,0.5)' }}>
                        <div style={{ padding: '4px 14px 8px', borderBottom: '1px solid #2a2a2a', marginBottom: '4px' }}>
                          <div style={{ fontSize: '11px', color: '#555', textTransform: 'uppercase', letterSpacing: '0.07em', fontFamily: 'Arial, sans-serif' }}>Balance</div>
                          <div style={{ fontSize: '20px', fontWeight: '700', color: T.white, fontFamily: 'Arial, sans-serif' }}>{tokenBalance} tokens</div>
                        </div>
                        <div onClick={() => { setTokenDropOpen(false); navigate('/tokens/purchase'); }} style={{ padding: '8px 14px', fontSize: '13px', color: T.accent, cursor: 'pointer', fontFamily: 'Arial, sans-serif', fontWeight: '700' }} onMouseEnter={e => e.currentTarget.style.background = '#262626'} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                          + Buy More Article Credits
                        </div>
                      </div>
                    )}
                  </div>
                )}
                {siloHref && <Link to={siloHref} style={{ background: T.accent, color: T.white, padding: '9px 18px', borderRadius: '5px', textDecoration: 'none', fontSize: '13px', fontWeight: '700', fontFamily: 'Arial, sans-serif', letterSpacing: '0.02em', whiteSpace: 'nowrap' }}>{userType === 'company' ? 'Admin Page' : 'My Silo'}</Link>}
              </>
            ) : (
              <>
                <Link to="/login" style={{ color: '#ccc', textDecoration: 'none', fontSize: '13px', fontFamily: 'Arial, sans-serif', fontWeight: '700', padding: '8px 15px', border: '1px solid #3a3a3a', borderRadius: '5px' }} onMouseEnter={e => { e.currentTarget.style.background = '#222'; e.currentTarget.style.color = T.white; }} onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#ccc'; }}>Login</Link>
                <Link to="/signup/role" style={{ background: T.accent, color: T.white, textDecoration: 'none', fontSize: '13px', fontFamily: 'Arial, sans-serif', fontWeight: '700', padding: '9px 18px', borderRadius: '5px', whiteSpace: 'nowrap' }}>Get Started</Link>
              </>
            )}
          </div>
          <div style={{ gridColumn: 2, gridRow: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#13273F', borderBottom: '2px solid #000', position: 'relative' }}>
            {GENRE_ROUTES.map(({ label, to, color }) => (
              <Link key={label} to={to} style={{ color: '#aaa', textDecoration: 'none', fontSize: '11px', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '0.08em', padding: '0 20px', height: '100%', display: 'flex', alignItems: 'center', fontFamily: 'Arial, sans-serif', transition: 'background 0.12s, color 0.12s' }}
                onMouseEnter={e => { e.currentTarget.style.background = color; e.currentTarget.style.color = T.white; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#aaa'; }}
              >{label}</Link>
            ))}
            <div style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)' }}>
              <NavSearchWidget searchQuery={searchQuery} setSearchQuery={setSearchQuery} setSearchResults={setSearchResults} />
            </div>
          </div>
        </nav>
      )}

      {/* ── Search bar (mobile only) ── */}
      {isMobile && <div style={{ background: '#f4f4f4', borderBottom: '1px solid #e0e0e0', padding: '10px 14px' }}>
        <div style={{ maxWidth: '1280px', margin: '0 auto', position: 'relative' }}>
          <input
            type="text"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            placeholder="Search articles, publishers, topics…"
            style={{ width: '100%', boxSizing: 'border-box', padding: '9px 40px 9px 14px', fontSize: '13px', fontFamily: 'Arial, sans-serif', border: '1.5px solid #ddd', borderRadius: '5px', background: '#fff', color: '#111', outline: 'none' }}
            onFocus={e => { e.currentTarget.style.borderColor = '#13273F'; }}
            onBlur={e => { e.currentTarget.style.borderColor = '#ddd'; }}
          />
          {searchQuery
            ? <button onClick={() => { setSearchQuery(''); setSearchResults(null); }}
                style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', fontSize: '16px', color: '#aaa', lineHeight: 1, padding: '2px 4px' }}>×</button>
            : <span style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', fontSize: '14px', color: '#bbb', pointerEvents: 'none' }}>🔍</span>
          }
        </div>
      </div>}

      {/* ── Search overlay ── */}
      {(searchQuery.trim().length >= 2) && (
        <SearchOverlay
          results={searchResults}
          loading={searchLoading}
          query={searchQuery.trim()}
          onClose={() => { setSearchQuery(''); setSearchResults(null); }}
          go={go}
          isMobile={isMobile}
        />
      )}

      {/* ── Mobile body ── */}
      {isMobile && (
        <div style={{ padding: '12px' }}>
          {loading
            ? [...Array(6)].map((_, i) => <div key={i} style={{ height: '60px', background: T.lightBg, borderRadius: '3px', marginBottom: '10px' }} />)
            : allArticles.map(a => {
                const color = GENRE_COLOR[a.genre_tree?.main] || T.accent;
                return (
                  <div key={a._id} onClick={() => go(a)} style={{ display: 'flex', gap: '10px', marginBottom: '14px', paddingBottom: '14px', borderBottom: `1px solid ${T.border}`, cursor: 'pointer' }}>
                    {a.headline_image && (
                      <div style={{ width: '90px', height: '68px', flexShrink: 0, overflow: 'hidden', borderRadius: '3px' }}>
                        <img src={a.headline_image} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top' }} />
                      </div>
                    )}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '10px', fontWeight: '800', color, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '3px', fontFamily: 'Arial, sans-serif' }}>{a.genre_tree?.main}</div>
                      <div style={{ fontSize: '14px', fontWeight: '700', color: T.headText, lineHeight: '19px', fontFamily: 'Georgia, serif', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{a.title}</div>
                      <div style={{ fontSize: '11px', color: T.meta, marginTop: '4px', fontFamily: 'Arial, sans-serif' }}>{a.company_name} · {new Date(a.date_created).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</div>
                    </div>
                  </div>
                );
              })
          }
        </div>
      )}

      {/* ── Desktop body ── */}
      {!isMobile && (
        <div style={{ maxWidth: '1280px', margin: '0 auto', padding: '28px 32px 60px' }}>

          {/* Top Stories */}
          <SectionDivider label="Top Stories" color={T.header} margin="0 50px 44px" />

          {/* Hero + 2×2 grid */}
          {loading ? (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '8px' }}>
              <Skeleton h={480} />
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                {[1,2,3,4].map(n => <Skeleton key={n} h={230} />)}
              </div>
            </div>
          ) : heroArticle ? (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '8px' }}>
              <div style={{ minHeight: '480px' }}>
                <HeroCard article={heroArticle} onClick={() => go(heroArticle)} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gridTemplateRows: '1fr 1fr', gap: '12px' }}>
                {sideArticles.map(a => <GridCard key={a._id} article={a} onClick={() => go(a)} />)}
              </div>
            </div>
          ) : null}

          {/* Local News */}
          <SectionDivider label="Local News" color="#27ae60" />
          <SectionCarousel articles={localArticles} color="#27ae60" go={go} viewAllTo="/local-news" />

          {/* Trending */}
          <SectionDivider label="Trending" color="#e67e22" />
          <SectionCarousel articles={trendSlice} color="#e67e22" go={go} max={12} />

          {/* State News */}
          <SectionDivider label="State News" color="#4db2ec" />
          <SectionCarousel articles={stateArticles} color="#4db2ec" go={go} viewAllTo="/state-news" />

          {/* National News */}
          <SectionDivider label="National News" color="#dd3333" />
          <SectionCarousel articles={nationalArticles} color="#dd3333" go={go} viewAllTo="/national-news" />

          {/* Publishers */}
          <SectionDivider label="Publishers" color="#13273F" />
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px' }}>
            {publishers.map(pub => (
              <PublisherCard key={pub.name} publisher={pub} go={go} />
            ))}
          </div>

        </div>
      )}

      {/* ── Footer ── */}
      <footer style={{ background: '#13273F', color: T.white, padding: isMobile ? '32px 20px 0' : '48px 52px 0' }}>
        {!isMobile && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '48px', paddingBottom: '32px' }}>
            <div>
              <img src={orangeLogo} alt="PubSilo" style={{ height: '42px', objectFit: 'contain', marginBottom: '18px' }} />
            </div>
            <div>
              <div style={{ fontSize: '11px', fontWeight: '900', color: T.white, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '20px', paddingBottom: '10px', borderBottom: '1px solid #1e1e1e', fontFamily: 'Arial, sans-serif' }}>Explore</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                {[{ label: 'Privacy Policy', to: '/privacy' }, { label: 'Terms of Service', to: '/terms' }, { label: 'Help Center', to: '/help' }, { label: 'Become a Publisher', to: '/company-contact' }].map(({ label, to }) => (
                  <Link key={label} to={to} style={{ fontSize: '13px', color: '#666', textDecoration: 'none', fontFamily: 'Arial, sans-serif', lineHeight: '28px' }} onMouseEnter={e => e.currentTarget.style.color = T.white} onMouseLeave={e => e.currentTarget.style.color = '#666'}>{label}</Link>
                ))}
              </div>
            </div>
            <div>
              <div style={{ fontSize: '11px', fontWeight: '900', color: T.white, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '20px', paddingBottom: '10px', borderBottom: '1px solid #1e1e1e', fontFamily: 'Arial, sans-serif' }}>Contact</div>
              <div style={{ fontSize: '13px', color: '#555', fontFamily: 'Arial, sans-serif', lineHeight: '28px' }}>
                <div>hello@pubsilo.com</div>
                <div>publishers@pubsilo.com</div>
                <div>ads@pubsilo.com</div>
              </div>
            </div>
          </div>
        )}
        {isMobile && (
          <div style={{ paddingBottom: '24px' }}>
            <img src={orangeLogo} alt="PubSilo" style={{ height: '36px', objectFit: 'contain', marginBottom: '14px' }} />
            <div style={{ fontSize: '13px', color: '#555', fontFamily: 'Arial, sans-serif', lineHeight: '24px' }}>
              <div>hello@pubsilo.com</div>
              <div>publishers@pubsilo.com</div>
            </div>
          </div>
        )}
        <div style={{ background: '#080808', margin: isMobile ? '0 -20px' : '0 -52px', padding: '16px 52px', textAlign: 'center' }}>
          <span style={{ fontSize: '12px', color: '#444', fontFamily: 'Arial, sans-serif' }}>© {new Date().getFullYear()} PubSilo, LLC · All rights reserved</span>
        </div>
      </footer>

    </div>
  );
}
